import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import React from "react";
import {
  AppShell,
  AppShellProvider,
  useAppShell,
} from "@salestrak/ui";

import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { INBOX_SUBSCRIPTIONS } from "#app/hooks/inbox/inbox-subscription";
import { useRegisterSubscriptions } from "#app/lib/subscriptions/subscription-provider";
import { useWorkspaceRouteValidation } from "#app/lib/hooks/use-workspace-route-validation";
import { useTrackScreenView } from "#app/lib/analytics";
import { SettingsSidebar } from "#app/components/navigation/settings-sidebar";
import { GlobalSearchDialog } from "#app/components/global-search/global-search-dialog";
import { useCtrlKShortcut } from "#app/components/global-search/use-ctrl-k-shortcut";
import { EntityDetailDialogs } from "#app/components/entity";
import { IssueViewDialogs } from "#app/components/issue-view";
import { AutomationsDialog } from "#app/lib/automations-dialog-context";
import { AutomationRunsDialogMount } from "#app/lib/automation-runs-dialog-context";
import { CustomFieldsDialog } from "#app/lib/custom-fields-dialog-context";
import { IntegrationsDialog } from "#app/lib/integrations-dialog-context";
import { ItemShareDialogMount } from "#app/lib/item-share-dialog-context";
import { MentionAccessDialogMount } from "#app/lib/mention-access-dialog-context";
import { SiteHeader } from "#app/components/navigation/site-header";
import {
  MODULE_RAIL_WIDTH,
  MODULE_RAIL_WIDTH_COMPACT,
  ModuleRail,
  useModuleRail,
} from "#app/components/navigation/module-rail";
import { HomeModuleMenu } from "#app/components/home/home-module-menu";
import { WorkflowsModuleMenu } from "#app/components/workflows/workflows-module-menu";
import { TeamsModuleMenu } from "#app/components/teams/teams-module-menu";
import { WidgetProviders } from "#app/widgets/widget-providers";

const PAGE_TITLES: Record<string, string> = {
  "/": "Home",
  "/my-work": "My Work",
  "/inbox": "Inbox",
  "/map": "Map",
  "/members": "Members",
  "/teams": "Teams",
  "/workflows": "Workflows",
  "/settings/preferences": "Preferences",
  "/settings/profile": "Profile",
  "/settings/access": "Access",
  "/settings/issue-types": "Project Types",
  "/settings/updates": "Updates",
  "/settings/trash": "Trash",
  "/settings/members": "Members",
  "/settings/teams": "Teams",
  "/settings/workflows": "Workflows",
  "/settings/api": "API",
};

// The persisted `sidebar-prefs.width` is the module-menu PANE width — the
// module rail is a separate column inside the same Sidebar, so compact mode
// narrows the rail without resizing the pane. The desktop drag seam and the
// rendered width both clamp to [MIN, MAX], so any persisted width outside
// those bounds is brought back into range on read.
const MIN_SIDEBAR_PANE_WIDTH = 210;
const MAX_SIDEBAR_PANE_WIDTH = 480;
const DEFAULT_SIDEBAR_PANE_WIDTH = 230;

function ShortcutListener() {
  // Mobile: ⌘K opens the global-search drawer (inert on desktop).
  // Desktop ⌘K focuses the inline header search bar (handled in GlobalSearchBar).
  useCtrlKShortcut();
  return null;
}

/**
 * Auto-closes the mobile drawer on route change — except when the module rail
 * drove the navigation (switching modules keeps the drawer open so the user
 * can pick from the new module menu).
 *
 * It lives inside `AppShellProvider` because the drawer's open state is the
 * shell's own `openMobile`, which only descendants can reach. Desktop `open`
 * is a separate, persisted preference the layout still owns.
 */
function MobileDrawerAutoClose({ pathname }: { pathname: string }) {
  const { isMobile, setOpenMobile } = useAppShell();
  const { keepDrawerOpenOnRailNav } = useModuleRail();
  const prevPathname = React.useRef(pathname);

  React.useEffect(() => {
    if (pathname === prevPathname.current) return;
    prevPathname.current = pathname;
    if (!isMobile) return;
    if (keepDrawerOpenOnRailNav.current) {
      keepDrawerOpenOnRailNav.current = false;
    } else {
      setOpenMobile(false);
    }
  }, [pathname, isMobile, keepDrawerOpenOnRailNav, setOpenMobile]);

  return null;
}

function WorkspaceLayout() {
  const { activeWorkspace } = useWorkspace();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { activeTileId, keepDrawerOpenOnRailNav, compact } = useModuleRail();
  const { state: sidebarPrefs, update: updateSidebarPrefs } = usePersistedState(
    "sidebar-prefs",
    { width: DEFAULT_SIDEBAR_PANE_WIDTH, expanded: true },
  );
  const handleSidebarWidthChange = React.useCallback(
    (width: number) => updateSidebarPrefs({ width }),
    [updateSidebarPrefs],
  );

  // Desktop expanded-vs-collapsed only. The mobile drawer is the shell's own
  // `openMobile` (ephemeral, never persisted), so it is a separate state from
  // this one. Seed from the synchronously-read preference so the first paint
  // already matches it.
  const [open, setOpen] = React.useState(() => sidebarPrefs.expanded);
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      updateSidebarPrefs({ expanded: next });
    },
    [updateSidebarPrefs],
  );

  useWorkspaceRouteValidation();
  useRegisterSubscriptions(INBOX_SUBSCRIPTIONS);
  useTrackScreenView();

  const isSettingsRoute = pathname.split("/").includes("settings");

  const routePath = activeWorkspace?.urlPath
    ? pathname.replace(`/${activeWorkspace.urlPath}`, "") || "/"
    : pathname;

  React.useEffect(() => {
    const title = PAGE_TITLES[routePath];
    if (isSettingsRoute) {
      document.title = title
        ? `${title} - Settings - SalesTrak`
        : "Settings - SalesTrak";
    } else {
      document.title = title ? `${title} - SalesTrak` : "SalesTrak";
    }
    // `routePath` is derived from the pathname and the workspace slug — the
    // two things this used to list separately.
  }, [routePath, isSettingsRoute]);

  // The active module-rail tile drives which module menu fills the sidebar
  // pane. The "map" tile is special: it shows no module menu (the map owns
  // the full content area). Settings routes keep their own dedicated sidebar.
  const moduleMenu = React.useMemo(() => {
    switch (activeTileId) {
      case "home":
        return <HomeModuleMenu />;
      case "map":
        return null;
      case "workflows":
        return <WorkflowsModuleMenu />;
      case "teams":
        return <TeamsModuleMenu />;
      default:
        return null;
    }
  }, [activeTileId]);

  const sidebarContent = isSettingsRoute ? <SettingsSidebar /> : moduleMenu;

  // Compact mode renders icon-only tiles, so the rail column narrows.
  const railWidth = compact ? MODULE_RAIL_WIDTH_COMPACT : MODULE_RAIL_WIDTH;
  const paneWidth = Math.min(
    MAX_SIDEBAR_PANE_WIDTH,
    Math.max(MIN_SIDEBAR_PANE_WIDTH, sidebarPrefs.width),
  );

  return (
    <WidgetProviders>
      <ShortcutListener />
      {/*
        * The shell: a full-width SiteHeader pinned to the top, then a row of
        * module rail + module-menu pane + inset content. Collapsing (`open`
        * false) clips the pane away and leaves the rail. The rail/pane split
        * is app domain; AppShell owns the surrounding layout and the mobile
        * drawer. The pane resize seam belongs to the pane component itself
        * (ModuleMenu / SettingsSidebar) — it reads and writes the width
        * published here through AppShellProvider.
        */}
      <AppShellProvider
        maxSidebarWidth={MAX_SIDEBAR_PANE_WIDTH}
        minSidebarWidth={MIN_SIDEBAR_PANE_WIDTH}
        onOpenChange={handleOpenChange}
        onSidebarWidthChange={handleSidebarWidthChange}
        open={open}
        sidebarWidth={paneWidth}
      >
        <MobileDrawerAutoClose pathname={pathname} />
        <AppShell
          moduleRail={<ModuleRail />}
          moduleRailWidth={railWidth}
          sidebar={sidebarContent}
          siteHeader={<SiteHeader />}
        >
          <Outlet />
        </AppShell>
      </AppShellProvider>
      <GlobalSearchDialog />
      <IssueViewDialogs />
      <EntityDetailDialogs />
      <CustomFieldsDialog />
      <IntegrationsDialog />
      <AutomationsDialog />
      <AutomationRunsDialogMount />
      <ItemShareDialogMount />
      <MentionAccessDialogMount />
    </WidgetProviders>
  );
}

export const Route = createFileRoute("/(app)/$workspace")({
  component: WorkspaceLayout,
});
